import "@/lib/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ModuleProvider } from "@/context/ModuleContext";
import AppLayout from "@/components/AppLayout";
import InstallAppButton from "@/components/InstallAppButton";
import { registerPwa } from "@/lib/pwa";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Prices from "@/pages/Prices";
import Settings from "@/pages/Settings";
import References from "@/pages/References";
import ElectricalModule from "@/pages/ElectricalModule";
import Annotator from "@/pages/Annotator";
import Compare from "@/pages/Compare";
import ProjectWizard from "@/pages/ProjectWizard";
import Analyzer from "@/pages/Analyzer";
import PublicReport from "@/pages/PublicReport";
import ProjectChat from "@/pages/ProjectChat";
import PublicChat from "@/pages/PublicChat";
import { PrivacyPolicy, TermsOfUse, Compliance } from "@/pages/Legal";
import HelpChat from "@/components/HelpChat";

registerPwa();

function AppRouter() {
  const location = useLocation();
  // Public share links bypass all auth completely
  if (location.pathname.startsWith("/public/report/")) {
    return (
      <Routes>
        <Route path="/public/report/:token" element={<PublicReport />} />
      </Routes>
    );
  }
  if (location.pathname.startsWith("/public/chat/")) {
    return (
      <Routes>
        <Route path="/public/chat/:token" element={<PublicChat />} />
      </Routes>
    );
  }
  // Emergent Google Auth returns user with #session_id=... in the URL fragment.
  // We must process this BEFORE the normal auth check runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/terms" element={<TermsOfUse />} />
      <Route path="/legal/compliance" element={<Compliance />} />
      <Route path="/" element={<AppLayout><Dashboard /></AppLayout>} />
      <Route path="/projects" element={<AppLayout><Projects /></AppLayout>} />
      <Route path="/projects/new" element={<AppLayout><ProjectWizard /></AppLayout>} />
      <Route path="/projects/:projectId" element={<AppLayout><ProjectDetail /></AppLayout>} />
      <Route path="/electrical" element={<AppLayout><ElectricalModule /></AppLayout>} />
      <Route path="/annotator/:estimationId" element={<AppLayout><Annotator /></AppLayout>} />
      <Route path="/projects/:projectId/compare" element={<AppLayout><Compare /></AppLayout>} />
      <Route path="/projects/:projectId/analyzer" element={<AppLayout><Analyzer /></AppLayout>} />
      <Route path="/projects/:projectId/chat" element={<AppLayout><ProjectChat /></AppLayout>} />
      <Route path="/prices" element={<AppLayout><Prices /></AppLayout>} />
      <Route path="/references" element={<AppLayout><References /></AppLayout>} />
      <Route path="/settings" element={<AppLayout><Settings /></AppLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ModuleProvider>
        <BrowserRouter>
          <AppRouter />
          <InstallAppButton />
          <HelpChat />
        </BrowserRouter>
      </ModuleProvider>
    </AuthProvider>
  );
}

export default App;
